// === Task Completion (per calendar year) ===
//
// A task is "done" only for the calendar year in which it was ticked. On
// Jan 1 every task is open again, matching the yearly rhythm of the herd
// health calendar. State: store.completions = { [taskId]: year }.

import store, { STORAGE_KEY_DONE, LEGACY_DONE_KEY } from '../state/store.js';
import { getItem, setItem } from './storage-service.js';

function currentYear() {
  return new Date().getFullYear();
}

// Tasks flagged meta.recurrence === 'monthly' must be re-done each month they
// appear in, so their done state is keyed per displayed month. All other tasks
// are keyed by id alone (done once for the whole year).
function isMonthlyRecurring(id) {
  const item = store.seed.find(i => i.id === id);
  return item?.meta?.recurrence === 'monthly';
}

function completionKey(id) {
  return isMonthlyRecurring(id) ? `${id}::${store.currentMonth}` : id;
}

// Load completions, migrating the old v14 format (a flat array of ids, no
// year) by assuming those were completed in the current year. Also drops any
// entries from previous years so storage stays small.
export function loadCompletions() {
  const saved = getItem(STORAGE_KEY_DONE);
  if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
    store.completions = saved;
  } else {
    const legacy = getItem(LEGACY_DONE_KEY);
    if (Array.isArray(legacy)) {
      const yr = currentYear();
      store.completions = {};
      legacy.forEach(id => { if (typeof id === 'string') store.completions[id] = yr; });
    } else {
      store.completions = {};
    }
  }
  purgeOldYears();
  setItem(STORAGE_KEY_DONE, store.completions);
}

function purgeOldYears() {
  const yr = currentYear();
  for (const id of Object.keys(store.completions)) {
    if (store.completions[id] !== yr) delete store.completions[id];
  }
}

export function isDone(id) {
  return store.completions[completionKey(id)] === currentYear();
}

// Toggle done state for the current year (per month for recurring tasks).
// Returns the new done state.
export function toggleDone(id) {
  const key = completionKey(id);
  if (store.completions[key] === currentYear()) {
    delete store.completions[key];
  } else {
    store.completions[key] = currentYear();
  }
  setItem(STORAGE_KEY_DONE, store.completions);
  return isDone(id);
}
